import React from "react";
import ChrWidgetBody from "../Basic/Widget/ChrWidgetBody";
import ChrWidgetContainer from "../Basic/Widget/ChrWidgetContainer";
import ChrWidgetHeader from "../Basic/Widget/ChrWidgetHeader";
import ChrWidgetFooter from "../Basic/Widget/ChrWidgetFooter";
import useRemoteDataFetcher from "../../../../hooks/generic/PageUI/useRemoteDataFetcher";
import * as ArrayExtensions from "../../../../utils/CoreExtensions/ArrayExtensions";
import { anyPositionBasedActionPresent } from "../../Utils/actionUtils";
import useWidgetLoadTracker from "../../../../hooks/generic/Utils/useWidgetLoadTracker";
import ChrWidgetLoaders from "../Basic/Widget/ChrWidgetLoaders";

/*
props = {
  header: <string | component>,
  helpText: <string>,
  footer: <string | component>,
  children: <component>,
  headerActions: [{ pinned: [<component>,...], notPinned: [<component>,...] },...],
  footerActions: [{ pinned: [<component>,...], notPinned: [<component>,...] },...],
  remote: <boolean>,
  remoteOptions: {
    url: <URL>,
    urlOptions: <JSON>,
    component: <component>,
    fetchOnlyOnce: <boolean>
  }
}
*/

const ChrWidget = (props) => {
  const {
    remote,
    remoteOptions = {},
    children,
    header,
    helpText,
    footer,
    headerActions,
    footerActions,
    loadersCount,
    loadersRowCount = 1,
    additionalLoaderClasses,
    headerActionsClass,
    footerActionsClass,
    widgetSpecificHeaderClasses,
    trackOnLoad = false,
    widgetUtils,
    waitDuringMultipleRequests = false,
    widgetLoader = undefined,
    noContainer = false
  } = props;
  const { fetchCondition = (() => remote) } = props;
  const { remoteData, contentLoaded } = useRemoteDataFetcher({
    fetchCondition,
    isContentLoaded: !!children,
    remoteOptions: remoteOptions,
    fetchOnlyOnce: remoteOptions.fetchOnlyOnce,
    dependencies: [remoteOptions],
    waitForAll: waitDuringMultipleRequests
  });
  const widgetContent = remote ? <remoteOptions.component { ...remoteData } widgetUtils={ widgetUtils } /> : children;
  const hideWidget = remote && contentLoaded && ArrayExtensions.isEmpty(remoteData.metrics);
  const showHeader = !!(header || (headerActions && anyPositionBasedActionPresent(headerActions)));
  const showFooter = !!(footer || (footerActions && anyPositionBasedActionPresent(footerActions)));
  useWidgetLoadTracker({ trackOnLoad, contentLoaded, remote, remoteData });

  const renderHeader = () => (
    showHeader && (
      <ChrWidgetHeader
        actions={headerActions}
        helpText={helpText}
        actionsClass={headerActionsClass}
        widgetSpecificHeaderClasses={widgetSpecificHeaderClasses}
      >
        {header}
      </ChrWidgetHeader>
    )
  );

  const renderBody = () => (
    <ChrWidgetBody>
      {contentLoaded
        ? widgetContent
        : (widgetLoader || (
          <ChrWidgetLoaders
            rows={loadersRowCount}
            columns={loadersCount}
            containerClassName=""
            additionalClassNames={additionalLoaderClasses}
          />
        ))
      }
    </ChrWidgetBody>
  );

  const renderFooter = () => (
    showFooter && (
      <ChrWidgetFooter
        actions={footerActions}
        actionsClass={footerActionsClass}
      >
        {footer}
      </ChrWidgetFooter>
    )
  );

  return (
    <>
      {!hideWidget && (
        noContainer ? (
          <>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
          </>
        ) : (
          <ChrWidgetContainer className={props.className}>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
          </ChrWidgetContainer>
        )
      )}
    </>
  );
};

export default ChrWidget;
